import { useEffect } from "react";

import { UISubscriptionStatus } from "common/types";
import { useNavigate } from "common/router/hooks";
import Navigate from "common/router/Navigate";
import { OnboardingClientEventKind } from "common/types/onboarding";

import {
  IOnboardingSubscription,
  OnboardingSubscriptionsStep,
  OnboardingSubscriptionsStepParams,
} from "client/onboarding/types";
import Button from "client/common/components/buttons/Button";
import Status from "client/common/components/content/Status";
import { getColorForStatus } from "client/onboarding/utils";
import PillButton from "client/common/components/buttons/PillButton";
import { useOnboardingEventsMutation } from "client/onboarding/hooks";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import { urls } from "client/routes";
import { useClinic } from "client/clinics/hooks";
import { genSubscriptionDiscountContent } from "client/subscriptions/utils";

import StepLayout from "../OnboardingSubscriptionsStepLayout";
import OnboardingSubscriptionItem from "../OnboardingSubscriptionItem";

import styles from "../../styles/OnboardingSubscriptionsListStep.module.less";

const OnboardingSubscriptionsListImportedItem = ({
  subscription,
}: {
  subscription: IOnboardingSubscription;
}) => {
  const navigate = useNavigate();
  const isSaving = subscription.first_discount_value > 0;

  return (
    <div>
      <OnboardingSubscriptionItem subscription={subscription}>
        <OnboardingSubscriptionItem.Pricing subscription={subscription} />
      </OnboardingSubscriptionItem>
      <div className={styles.buttons}>
        <Button
          onClick={() =>
            navigate(
              urls.onboarding.subscriptions.detail({
                params: {
                  id: subscription.id,
                  step: OnboardingSubscriptionsStep.QuantityFrequency,
                },
              }),
            )
          }
        >
          Continue {isSaving && "with the savings!"}
        </Button>
        <Button
          variant="secondary"
          onClick={() =>
            navigate(
              urls.onboarding.subscriptions.detail({
                params: {
                  id: subscription.id,
                  step: OnboardingSubscriptionsStep.Cancel,
                },
              }),
            )
          }
        >
          Cancel my Autoship
        </Button>
      </div>
    </div>
  );
};

const OnboardingSubscriptionsListUpdatedItem = ({
  subscription,
}: {
  subscription: IOnboardingSubscription;
}) => {
  const navigate = useNavigate();
  return (
    <OnboardingSubscriptionItem
      subscription={subscription}
      renderSideContent={() => (
        <div className={styles.side}>
          <Status color={getColorForStatus(subscription.visible_status)}>
            {subscription.visible_status_display}
          </Status>
          {subscription.visible_status !== UISubscriptionStatus.Canceled && (
            <div className={styles.updatedButtons}>
              <PillButton
                size="s"
                variant="secondary"
                onClick={() =>
                  navigate(
                    urls.onboarding.subscriptions.detail({
                      params: {
                        id: subscription.id,
                        step: OnboardingSubscriptionsStep.Cancel,
                      },
                    }),
                  )
                }
              >
                Cancel
              </PillButton>
              <PillButton
                size="s"
                onClick={() =>
                  navigate(
                    urls.onboarding.subscriptions.detail({
                      params: {
                        id: subscription.id,
                        step: OnboardingSubscriptionsStep.QuantityFrequency,
                      },
                    }),
                  )
                }
              >
                Update
              </PillButton>
            </div>
          )}
        </div>
      )}
    />
  );
};

const OnboardingSubscriptionsListStep = ({
  client,
  onboarding,
  stepper,
  rootDest,
}: OnboardingSubscriptionsStepParams) => {
  // if event creation errors, fail silently
  const { mutate: createEvent, isLoading: isEventCreating } = useOnboardingEventsMutation({});
  const clinic = useClinic();

  const shouldRedirect = onboarding.subscriptions.every(
    (subscription) => subscription.visible_status !== UISubscriptionStatus.Imported,
  );

  useEffect(() => {
    if (!shouldRedirect && !isEventCreating) {
      createEvent({ kind: OnboardingClientEventKind.LinkOpened });
    }
    // disabling lint to allow the event creation to just occur once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (shouldRedirect) {
    return <Navigate dest={rootDest} />;
  }

  if (isEventCreating) {
    return <LoadingContainer />;
  }

  const updatedSubscriptions = onboarding.subscriptions.filter(
    (subscription) => subscription.visible_status !== UISubscriptionStatus.Imported,
  );
  return (
    <StepLayout stepper={stepper}>
      <StepLayout.Title>Hi {client.primary_client_user.first_name}!</StepLayout.Title>
      <StepLayout.Description>
        <div>
          We&apos;ve updated to a new online store to help lower your prices and improve your
          purchasing experience!
        </div>
        <div>
          To continue receiving your Autoship order, we&apos;ll need you to confirm your frequency
          and update your billing preferences...
        </div>
      </StepLayout.Description>
      <div className={styles.importedSubscriptions}>
        {onboarding.subscriptions
          .filter((subscription) => subscription.visible_status === UISubscriptionStatus.Imported)
          .map((subscription) => (
            <OnboardingSubscriptionsListImportedItem
              key={subscription.id}
              subscription={subscription}
            />
          ))}
      </div>
      {!!updatedSubscriptions.length && (
        <div className={styles.updatedSubscriptions}>
          {updatedSubscriptions.map((subscription) => (
            <OnboardingSubscriptionsListUpdatedItem
              key={subscription.id}
              subscription={subscription}
            />
          ))}
        </div>
      )}
      <div className={styles.disclaimer}>
        {genSubscriptionDiscountContent(
          clinic.first_subscription_discount,
          clinic.recurring_subscription_discount,
          {
            none: () => "",
            onlyFirst: (first) => `You'll save up to ${first} off your first order. `,
            onlyRecurring: (recurring) => `You'll save up to ${recurring} off recurring orders. `,
            both: (first, recurring) =>
              `You'll save up to ${first} off your first order and ${recurring} off recurring orders. `,
          },
        )}
        Exclusions may apply.
      </div>
    </StepLayout>
  );
};

export default OnboardingSubscriptionsListStep;
